/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment content picker with chapter tree selection.
 *
 * @file AssignmentContentPicker.jsx
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import Icon from '../icons/Icon';
import { useChapterContent } from './ChapterContentContext';
import ChapterContentTree from './ChapterContentTree';
import { AssignmentsStylesRoot, classes } from '../assignments/styles/assignmentsStyles';

const ACTION_VARIANT_CLASS = {
  finish: 'finishButton',
  save: 'saveButton'
};

const AssignmentContentPicker = ({
  selectedChapters,
  onNodeToggle,
  summary,
  actions,
  onBack
}) => {
  const { textInfo } = useChapterContent();

  const {
    sectionNumbers = [],
    sections = 0,
    questions = 0,
    chaptersWithSelection = 0,
    showSectionsSummary = false
  } = summary || {};

  const renderAction = (action) => {
    if (!action.callback) {
      return null;
    }

    const disabled = Boolean(action.disabled);
    const buttonClass = classes[ACTION_VARIANT_CLASS[action.variant]] || classes.finishButton;

    const handleClick = () => {
      if (disabled) {
        return;
      }

      action.callback();
    };

    return (
      <Button
        type="button"
        className={buttonClass}
        onClick={handleClick}
        disabled={disabled}
      >
        <span>{action.text}</span>
      </Button>
    );
  };

  const renderHeader = () => (
    <div className={classes.contentPickerHeader}>
      <button
        type="button"
        className={classes.contentPickerBackButton}
        onClick={onBack}
        aria-label={textInfo.backLabel}
      >
        <Icon name="BackIcon" />
      </button>
      <h2 className={classes.contentPickerTitle}>{textInfo.contentHeaderTitle}</h2>
    </div>
  );

  const renderSectionChips = () => {
    if (!sectionNumbers.length) {
      return null;
    }

    return (
      <div className={classes.contentPickerSections}>
        <span className={classes.contentPickerSectionsLabel}>
          {textInfo.sectionsLabel}
        </span>
        {sectionNumbers.map((sectionNumber) => (
          <span key={sectionNumber} className={classes.contentPickerSectionChip}>
            {sectionNumber}
          </span>
        ))}
      </div>
    );
  };

  const renderTree = () => (
    <ChapterContentTree
      selectedNodes={selectedChapters}
      onToggleNode={onNodeToggle}
    />
  );

  const renderSummary = () => (
    <div className={classes.contentPickerSummary}>
      <span className={classes.contentPickerSummaryBadge}>
        {chaptersWithSelection} {textInfo.chaptersLabel}
      </span>
      {showSectionsSummary && (
        <span className={classes.contentPickerSummaryBadge}>
          {sections} {textInfo.sectionLabel}
        </span>
      )}
      <span className={classes.contentPickerSummaryBadge}>
        {questions} {textInfo.questionsLabel}
      </span>
    </div>
  );

  const renderActions = () => {
    if (!actions?.length) {
      return null;
    }

    return (
      <div className={classes.contentPickerActions}>
        {actions.map((action, index) => (
          <React.Fragment key={action.id || action.text || index}>
            {renderAction(action)}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <AssignmentsStylesRoot className={classes.contentPicker}>
      {renderHeader()}
      {renderSectionChips()}
      {renderTree()}
      {renderSummary()}
      {renderActions()}
    </AssignmentsStylesRoot>
  );
};

AssignmentContentPicker.propTypes = {
  selectedChapters: PropTypes.array,
  onNodeToggle: PropTypes.func.isRequired,
  summary: PropTypes.shape({
    selectedTitles: PropTypes.array,
    sectionNumbers: PropTypes.arrayOf(PropTypes.string),
    sections: PropTypes.number,
    questions: PropTypes.number,
    chaptersWithSelection: PropTypes.number,
    showSectionsSummary: PropTypes.bool
  }),
  actions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    text: PropTypes.string,
    variant: PropTypes.oneOf(['finish', 'save']),
    callback: PropTypes.func,
    disabled: PropTypes.bool
  })),
  onBack: PropTypes.func.isRequired
};

AssignmentContentPicker.defaultProps = {
  selectedChapters: [],
  summary: {
    selectedTitles: [],
    sectionNumbers: [],
    sections: 0,
    questions: 0,
    chaptersWithSelection: 0,
    showSectionsSummary: false
  },
  actions: []
};

export default AssignmentContentPicker;
